import {
    BrewInventoryIcon,
    BrewOnDeckIcon,
    BrewOnTapOutlineIcon,
    DepletionRateIcon,
    DepletionRateNavigationIcon,
    FilterIcon,
    LevelsNavigationIcon,
    OzIcon,
    PhotographIcon,
    SpeedometerIcon
} from "blx-icons"
import type { NavigationLinkItem } from "config/navigation/types/NavigationLinkItem"
import { BLXApplications } from "config/routing"
import { useHypertune } from "lib/feature-flags/generated/hypertune.react"
import { selectActivePpSubId, useAppSelector } from "services"
import usePPRoutes from "./usePPRoutes"

const buildNavigationLinkItem = (
    navItem: Omit<NavigationLinkItem, "application" | "displayType" | "isDisabled">
): NavigationLinkItem => ({
    ...navItem,
    application: BLXApplications.Operations,
    displayType: "link",
    isDisabled: false
})

interface PPRouteNavigationLinkItems {
    currentPPDashboardLinkItem: NavigationLinkItem
    onTapLinkItem: NavigationLinkItem
    onDeckLinkItem: NavigationLinkItem
    inventoryLinkItem: NavigationLinkItem
    throughputLinkItem: NavigationLinkItem
    levelAnalysisLinkItem: NavigationLinkItem
    depletionAnalysisLinkItem: NavigationLinkItem
    kickDateAnalysisLinkItem: NavigationLinkItem
    freshnessAnalysisLinkItem: NavigationLinkItem
    stylesOnTapLinkItem: NavigationLinkItem
    historyLinkItem: NavigationLinkItem
    brewStatsLinkItem: NavigationLinkItem
    workWeekAndHoursLinkItem: NavigationLinkItem
    pricingLinkItem: NavigationLinkItem
    locationDetailsLinkItem: NavigationLinkItem
    priorityBrandsLinkItem: NavigationLinkItem
    preferencesLinkItem: NavigationLinkItem
    draftProgramLinkItem: NavigationLinkItem
    batteryReplacementLinkItem: NavigationLinkItem
    generalLinkItem: NavigationLinkItem
    // Link Item Groups
    operationsAnalyticsConfig: NavigationLinkItem[]
    operationstApplicationNavigationConfig: NavigationLinkItem[]
    operationsSettingsConfig: NavigationLinkItem[]
}

/**
 * Utility function to check if a given PP subscription ID exists within a list of variance report access subscriptions.
 *
 * @param ppSubId - The PP subscription ID to check.
 * @param varianceReportAccessSubscriptions - An array of variance report access subscription IDs.
 * @returns `true` if the PP subscription ID exists in the list, otherwise `false`.
 */
// TODO: ENG-3657 - Remove Hypertune Flags for Variance Report Feature
const hasPPSub = (ppSubId: string, varianceReportAccessSubscriptions: string[]): boolean => {
    if (ppSubId.length === 0) return false
    if (varianceReportAccessSubscriptions.length === 0) return false

    if (typeof varianceReportAccessSubscriptions === "object") {
        return varianceReportAccessSubscriptions.includes(ppSubId)
    }

    return false
}

export const usePPRouteNavigationLinkItems = (): PPRouteNavigationLinkItems => {
    // TODO: ENG-3657 - Remove Hypertune Flags for Variance Report Feature
    const flags = useHypertune()
    const ppSubId = useAppSelector(selectActivePpSubId)
    const varianceReportingIsFiltered = flags.varianceReportingIsFiltered({ fallback: true })
    const varianceReportAccessSubscriptions = flags.varianceReportAccessSubscriptions({
        itemFallback: ""
    })

    const {
        staticRoutes,
        batteryReplacement,
        brewStats,
        currentPPDashboard,
        depletionAnalysis,
        draftProgram,
        freshnessAnalysis,
        general,
        history,
        inventory,
        kickDateAnalysis,
        levelAnalysis,
        locationDetails,
        onDeck,
        onTap,
        preferences,
        pricing,
        priorityBrands,
        stylesOnTap,
        throughput,
        varianceReport,
        workWeekAndHours
    } = usePPRoutes()

    const {
        batteryReplacementStatic,
        brewStatsStatic,
        depletionAnalysisStatic,
        draftProgramStatic,
        freshnessAnalysisStatic,
        generalStatic,
        historyStatic,
        inventoryStatic,
        kickDateAnalysisStatic,
        levelAnalysisStatic,
        locationDetailsStatic,
        onDeckStatic,
        onTapStatic,
        preferencesStatic,
        pricingStatic,
        priorityBrandsStatic,
        stylesOnTapStatic,
        throughputStatic,
        varianceReportStatic,
        workWeekAndHoursStatic
    } = staticRoutes

    const currentPPDashboardLinkItem = buildNavigationLinkItem({
        icon: SpeedometerIcon,
        label: "Dashboard",
        path: currentPPDashboard(),
        permittedRoles: ["admin", "owner", "staff"]
    })

    const onTapLinkItem = buildNavigationLinkItem({
        icon: BrewOnTapOutlineIcon,
        label: "On Tap",
        path: onTap(),
        styleMatchingPath: onTapStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const onDeckLinkItem = buildNavigationLinkItem({
        icon: BrewOnDeckIcon,
        label: "On Deck",
        path: onDeck(),
        styleMatchingPath: onDeckStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const inventoryLinkItem = buildNavigationLinkItem({
        icon: BrewInventoryIcon,
        label: "Inventory",
        path: inventory(),
        styleMatchingPath: inventoryStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const throughputLinkItem = buildNavigationLinkItem({
        icon: FilterIcon,
        label: "Throughput",
        path: throughput(),
        styleMatchingPath: throughputStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const levelAnalysisLinkItem = buildNavigationLinkItem({
        icon: LevelsNavigationIcon,
        label: "Level Analysis",
        path: levelAnalysis(),
        styleMatchingPath: levelAnalysisStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const depletionAnalysisLinkItem = buildNavigationLinkItem({
        icon: DepletionRateNavigationIcon,
        label: "Depletion Analysis",
        path: depletionAnalysis(),
        styleMatchingPath: depletionAnalysisStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const kickDateAnalysisLinkItem = buildNavigationLinkItem({
        icon: OzIcon,
        label: "Kick Date Analysis",
        path: kickDateAnalysis(),
        styleMatchingPath: kickDateAnalysisStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const varianceReportLinkItem = buildNavigationLinkItem({
        icon: DepletionRateIcon,
        label: "Variance Report",
        path: varianceReport(),
        styleMatchingPath: varianceReportStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const freshnessAnalysisLinkItem = buildNavigationLinkItem({
        icon: PhotographIcon,
        label: "Freshness Analysis",
        path: freshnessAnalysis(),
        styleMatchingPath: freshnessAnalysisStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const stylesOnTapLinkItem = buildNavigationLinkItem({
        icon: PhotographIcon,
        label: "Styles On Tap",
        path: stylesOnTap(),
        styleMatchingPath: stylesOnTapStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const historyLinkItem = buildNavigationLinkItem({
        icon: PhotographIcon,
        label: "History",
        path: history(),
        styleMatchingPath: historyStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const brewStatsLinkItem = buildNavigationLinkItem({
        icon: PhotographIcon,
        label: "Brew Stats",
        path: brewStats(),
        styleMatchingPath: brewStatsStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const workWeekAndHoursLinkItem = buildNavigationLinkItem({
        icon: null,
        label: "Work Week & Hours",
        path: workWeekAndHours(),
        styleMatchingPath: workWeekAndHoursStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const pricingLinkItem = buildNavigationLinkItem({
        icon: null,
        label: "Pricing",
        path: pricing(),
        styleMatchingPath: pricingStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const locationDetailsLinkItem = buildNavigationLinkItem({
        icon: null,
        label: "Location Details",
        path: locationDetails(),
        styleMatchingPath: locationDetailsStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const priorityBrandsLinkItem = buildNavigationLinkItem({
        icon: null,
        label: "Priority Brands",
        path: priorityBrands(),
        styleMatchingPath: priorityBrandsStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const preferencesLinkItem = buildNavigationLinkItem({
        icon: PhotographIcon,
        label: "Preferences",
        path: preferences(),
        styleMatchingPath: preferencesStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const draftProgramLinkItem = buildNavigationLinkItem({
        icon: null,
        label: "Draft Program",
        path: draftProgram(),
        styleMatchingPath: draftProgramStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const batteryReplacementLinkItem = buildNavigationLinkItem({
        icon: null,
        label: "Battery Replacement",
        path: batteryReplacement(),
        styleMatchingPath: batteryReplacementStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    const generalLinkItem = buildNavigationLinkItem({
        icon: PhotographIcon,
        label: "General",
        path: general(),
        styleMatchingPath: generalStatic,
        permittedRoles: ["admin", "owner", "staff"]
    })

    // Link Item Groups
    const operationstApplicationNavigationConfig = [
        currentPPDashboardLinkItem,
        onTapLinkItem,
        onDeckLinkItem,
        inventoryLinkItem
    ]

    // Analytics Link Items
    const operationsAnalyticsConfig = [
        throughputLinkItem,
        levelAnalysisLinkItem,
        depletionAnalysisLinkItem,
        kickDateAnalysisLinkItem
    ]

    // TODO: ENG-3657 - Remove Hypertune Flags for Variance Report Feature
    // If the flag is enabled, add the variance report link item to the analytics config
    if (!varianceReportingIsFiltered) {
        operationsAnalyticsConfig.push(varianceReportLinkItem)
    } else if (hasPPSub(ppSubId, varianceReportAccessSubscriptions)) {
        operationsAnalyticsConfig.push(varianceReportLinkItem)
    }

    const operationsSettingsConfig = [
        locationDetailsLinkItem,
        workWeekAndHoursLinkItem,
        pricingLinkItem,
        priorityBrandsLinkItem,
        draftProgramLinkItem,
        batteryReplacementLinkItem
    ]

    return {
        currentPPDashboardLinkItem,
        onTapLinkItem,
        onDeckLinkItem,
        inventoryLinkItem,
        throughputLinkItem,
        levelAnalysisLinkItem,
        depletionAnalysisLinkItem,
        kickDateAnalysisLinkItem,
        freshnessAnalysisLinkItem,
        stylesOnTapLinkItem,
        historyLinkItem,
        brewStatsLinkItem,
        workWeekAndHoursLinkItem,
        pricingLinkItem,
        locationDetailsLinkItem,
        priorityBrandsLinkItem,
        preferencesLinkItem,
        draftProgramLinkItem,
        batteryReplacementLinkItem,
        generalLinkItem,
        // Link Item Groups
        operationsAnalyticsConfig,
        operationstApplicationNavigationConfig,
        operationsSettingsConfig
    }
}
